// src/utils/cabinaDiRegia.js
// Motore condiviso della Cabina di Regia — la LOGICA vive qui una volta sola:
//  • valutaSegnaliCommessa: le regole di allarme, riusate anche da Suggerimenti Operativi
//    (tutte le commesse insieme, con i dati che già legge lui);
//  • analizzaCommessa: tutto quello che la Cabina di Regia mostra di UNA commessa
//    (salute, numeri chiave, settimana, varianti, fatturazione, ore, pratiche, chiusura).
// Funzioni PURE: nessuna chiamata a Firestore. I dati li legge (in tempo reale) il
// componente CabinaDiRegiaTab; qui si fanno solo i conti, così sono verificabili.
//
// SUL PROBLEMA DEI FALSI ALLARMI:
// Ogni regola può essere SILENZIATA per singola commessa, perché una commessa può avere un
// pattern di lavoro legittimo che una regola generica interpreterebbe come anomalia. Il
// silenziamento vive come array `regoleSilenziate` sul documento della commessa, e dalla
// Cabina di Regia si può sempre riattivare.
import { isTaskOverdue } from "./taskHelpers";
import { statoVariante, importoVariante, riepilogoVarianti, ORIGINI_VARIANTE } from "./varianti";
import { eCliente, senzaTipo, segnalazioneAperta } from "./corrispondenzaCliente";
import { toLocalDateStr } from "./dateUtils";
import { economiaSintetica, importoCommessa, numIt, dataMovimento, imponibileOda, STATI_ODA_CONTEGGIATI } from "./economiaCommessa";
import { FONTI_ORE, oreDaProgrammazione, oreDaRapportini, oreDaPresenze, riepilogoOre, vociConDiario, orePerSettimana, costiCalcolatiDaVoci, unisciCostiCalcolati } from "./oreCommessa";
import { uominiGiornoPresunti } from "./esecuzioneCommessa";
import { ditteDellaCommessa, testoUG } from "./ditteEsterne";
import { testoProblemiDitta } from "./documentiSubappaltatori";
import { calcolaSAL } from "./calcoloSAL";
import { righePratica, avanzamento, statoIter } from "./pratiche";
import { isNoleggioAttivo, costoNoleggio, TIPI_MEZZO_OPERA, scadenzaNoleggio, etichettaNoleggio, testoScadenzaNoleggio, rettificheDaVedere, testoRettifica } from "./noleggiUtils";

export const REGOLE = {
  PM_MANCANTE:            { id:"pm_mancante",            label:"Nessun Project Manager assegnato",   gravita:"alta"  },
  FINE_LAVORI_SUPERATA:   { id:"fine_lavori_superata",   label:"Fine lavori prevista superata",       gravita:"alta"  },
  TASK_IN_RITARDO:        { id:"task_in_ritardo",        label:"Attività in ritardo",                 gravita:"alta"  },
  MARGINE_A_RISCHIO:      { id:"margine_a_rischio",      label:"Margine sotto soglia",                gravita:"alta"  },
  NOLEGGIO_DA_DECIDERE:   { id:"noleggio_da_decidere",   label:"Noleggio oltre la fine prevista",     gravita:"alta"  },
  NOLEGGIO_SOSPESO:       { id:"noleggio_sospeso",       label:"Noleggio sospeso oltre la fine prevista", gravita:"media" },
  NOLEGGIO_RETTIFICATO:   { id:"noleggio_rettificato",   label:"Noleggio rettificato",                gravita:"media" },
  SICUREZZA_DA_COMPLETARE:{ id:"sicurezza_da_completare",label:"Sicurezza di cantiere da completare", gravita:"alta"  },
  DITTA_NON_IN_REGOLA:    { id:"ditta_non_in_regola",    label:"Impresa esterna con documenti non in regola", gravita:"alta" },
  DITTA_OLTRE_PREVISTO:   { id:"ditta_oltre_previsto",   label:"Ditta esterna oltre gli uomini-giorno previsti", gravita:"media" },
  COMMESSA_FERMA:         { id:"commessa_ferma",         label:"Commessa ferma",                      gravita:"media" },
  BUDGET_MANCANTE:        { id:"budget_mancante",        label:"Budget non ancora impostato",         gravita:"media" },
  CRONOPROGRAMMA_ASSENTE: { id:"cronoprogramma_assente", label:"Nessun Cronoprogramma registrato",     gravita:"media" },
  SAL_FERMO:              { id:"sal_fermo",              label:"SAL fermo da tempo",                  gravita:"media" },
  SEGNALAZIONE_APERTA:    { id:"segnalazione_aperta",    label:"Segnalazione cliente senza seguito",  gravita:"media" },
  VARIANTI_DA_VALORIZZARE:{ id:"varianti_da_valorizzare",label:"Varianti senza importo",     gravita:"media" },
  VARIANTI_DA_ACCETTARE:  { id:"varianti_da_accettare",  label:"Varianti eseguite non accettate",     gravita:"media" },
  PRATICA_FERMA:          { id:"pratica_ferma",          label:"Pratica ferma",                       gravita:"media" },
  VARIANTI_DA_FATTURARE:  { id:"varianti_da_fatturare",  label:"Varianti accettate non fatturate",  gravita:"bassa" },
  MATERIALI_IN_ATTESA:    { id:"materiali_in_attesa",    label:"Richieste al magazzino in attesa",    gravita:"bassa" },
  COMUNICAZIONI_SENZA_TIPO:{ id:"comunicazioni_senza_tipo", label:"Comunicazioni del cliente senza tipo", gravita:"bassa" },
  ORE_DIVERSE:            { id:"ore_diverse",            label:"Ore dei rapportini diverse da quelle contabilizzate", gravita:"bassa" },
};

export const SOGLIA_SAL_FERMO_GIORNI = 30;
export const SOGLIA_SEGNALAZIONE_GIORNI = 10;
export const SOGLIA_EXTRA_GIORNI = 15;
export const SOGLIA_MARGINE_RISCHIO = 15; // %, stessa soglia già usata in Commesse a Rischio Margine
export const SOGLIA_FERMA_GIORNI = 14;     // nessuna novità sulla commessa da più di due settimane
export const SOGLIA_MATERIALI_GIORNI = 3;  // richiesta al magazzino ancora in attesa
export const GIORNI_SETTIMANA = 7;

// Costo dagli Ordini di Acquisto: fonte unica in utils/economiaCommessa.js — qui restano
// esportati con lo stesso nome per chi li importava già da questo file.
export { STATI_ODA_CONTEGGIATI, imponibileOda, sommaCostiOda } from "./economiaCommessa";

// ── Date ───────────────────────────────────────────────────────────────────
/** Data in formato "aaaa-mm-gg" da stringa, Date o Timestamp di Firestore ("" se assente). */
export function isoDa(x) {
  if (!x) return "";
  if (typeof x === "string") return /^\d{4}-\d{2}-\d{2}/.test(x) ? x.slice(0, 10) : "";
  const d = x?.toDate ? x.toDate() : (x instanceof Date ? x : null);
  return d && !isNaN(d) ? toLocalDateStr(d) : "";
}
/** Giorni da a → b (positivo se b è dopo a). */
export function giorniTra(a, b) {
  return Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
}
export function piuGiorni(iso, n) {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return toLocalDateStr(d);
}
export function giorniDa(data) {
  const iso = isoDa(data);
  return iso ? giorniTra(iso, toLocalDateStr(new Date())) : null;
}
export const dataBreve = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

const chiusa = (p) => p?.statoCommessa === "chiusa";
const taskAperta = (t) => t.status !== "completata" && t.status !== "annullata";

/**
 * Le regole — funzione PURA: riceve i dati già pronti (per una commessa) e restituisce i
 * segnali. I campi di ctx oltre a quelli storici sono facoltativi: Suggerimenti Operativi
 * non li passa e quelle regole semplicemente non scattano lì.
 *
 * @param {object} projectData - il documento della commessa (projects/{id})
 * @param {object} ctx - { tasks, hasCronoprogramma, computoSAL, budget, costiSostenuti, segnalazioni, attivitaExtra,
 *                         giorniFerma?, praticheInRitardo?, sicurezza?, materialiInAttesa?, comunicazioniSenzaTipo?, oggi? }
 */
export function valutaSegnaliCommessa(projectData, ctx) {
  const regoleSilenziate = new Set(projectData?.regoleSilenziate || []);
  const segnali = [];
  const silenziati = [];
  const aggiungi = (regola, dettaglio, extra={}) => {
    if (regoleSilenziate.has(regola.id)) { silenziati.push(regola); return; }
    segnali.push({ ...regola, dettaglio, ...extra });
  };
  const oggi = ctx.oggi || toLocalDateStr(new Date());
  const giorniApertura = giorniDa(projectData?.createdAt);
  const tasks = ctx.tasks || [];

  if (!projectData?.responsabileUid) aggiungi(REGOLE.PM_MANCANTE, "Nessun responsabile assegnato a questa commessa.");

  const fine = isoDa(projectData?.dataFine);
  const aperte = tasks.filter(taskAperta);
  if (fine && fine < oggi && !chiusa(projectData) && aperte.length > 0) {
    const gg = giorniTra(fine, oggi);
    aggiungi(REGOLE.FINE_LAVORI_SUPERATA, `Fine lavori prevista il ${dataBreve(fine)} (${gg} giorn${gg===1?"o":"i"} fa), ${aperte.length} attività ancora apert${aperte.length===1?"a":"e"}.`, {giorni:gg});
  }

  const inRitardo = tasks.filter(t=>isTaskOverdue(t));
  if (inRitardo.length>0) aggiungi(REGOLE.TASK_IN_RITARDO, `${inRitardo.length} attività risulta${inRitardo.length===1?"":"no"} scadut${inRitardo.length===1?"a":"e"}.`, {conteggio:inRitardo.length});

  if (ctx.budget>0) {
    const margine = (ctx.budget-ctx.costiSostenuti)/ctx.budget*100;
    if (margine<SOGLIA_MARGINE_RISCHIO) aggiungi(REGOLE.MARGINE_A_RISCHIO, `Margine attuale al ${(margine).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%.`, {margine});
  }

  // Gru/piattaforme oltre la fine prevista (o aperte da molto senza data): il costo cresce
  // ogni giorno finché qualcuno non chiude il noleggio o lo proroga (utils/noleggiUtils.js).
  // I noleggi sospesi in questo momento hanno un avviso a parte: non costano finché sono
  // fermi, e la decisione (chiudere o prorogare) si prende alla ripresa.
  const daDecidereOra = (ctx.noleggiDaDecidere||[]).filter(x => !x.sospeso);
  const sospesiOra = (ctx.noleggiDaDecidere||[]).filter(x => x.sospeso);
  if (daDecidereOra.length>0) {
    const n = daDecidereOra.length, primo = daDecidereOra[0];
    aggiungi(REGOLE.NOLEGGIO_DA_DECIDERE, n === 1
      ? `${primo.etichetta}: ${primo.testo}`
      : `${n} noleggi da chiudere o prorogare. ${primo.etichetta}: ${primo.testo}`, {conteggio:n, giorni:primo.giorni||0});
  }
  if (sospesiOra.length>0) {
    const n = sospesiOra.length, primo = sospesiOra[0];
    aggiungi(REGOLE.NOLEGGIO_SOSPESO, n === 1
      ? `${primo.etichetta}: ${primo.testo}`
      : `${n} noleggi sospesi. ${primo.etichetta}: ${primo.testo}`, {conteggio:n});
  }

  // Rettifiche sui noleggi (data di fine cambiata a posteriori): restano finché un admin non le vede.
  if ((ctx.noleggiRettificati||[]).length>0) {
    const n = ctx.noleggiRettificati.length, primo = ctx.noleggiRettificati[0];
    aggiungi(REGOLE.NOLEGGIO_RETTIFICATO, `${n > 1 ? `${n} rettifiche da vedere. ` : ""}${primo.etichetta}: ${primo.testo}`, {conteggio:n});
  }

  if (ctx.sicurezza?.attivo) {
    aggiungi(REGOLE.SICUREZZA_DA_COMPLETARE, ctx.sicurezza.daConfigurare
      ? "Il responsabile deve ancora rispondere alle domande sul cantiere."
      : `${ctx.sicurezza.aperte} adempiment${ctx.sicurezza.aperte===1?"o":"i"} ancora da completare.`);
  }

  // Ditte esterne (W413): documenti scaduti o mancanti (piano Gold) e uomini-giorno reali
  // (Giornale di cantiere) oltre quelli previsti nella commessa
  if ((ctx.ditteNonInRegola||[]).length>0) {
    const n = ctx.ditteNonInRegola.length, primo = ctx.ditteNonInRegola[0];
    aggiungi(REGOLE.DITTA_NON_IN_REGOLA, `${n > 1 ? `${n} imprese esterne non in regola. ` : ""}${primo.nome}: ${primo.testo}.`, {conteggio:n});
  }
  if ((ctx.ditteOltre||[]).length>0) {
    const n = ctx.ditteOltre.length, primo = ctx.ditteOltre[0];
    aggiungi(REGOLE.DITTA_OLTRE_PREVISTO, `${n > 1 ? `${n} ditte oltre il previsto. ` : ""}${primo.nome}: ${testoUG(primo.reali)} dal Giornale di cantiere su ${testoUG(primo.presunti)} previsti.`, {conteggio:n});
  }

  if (typeof ctx.giorniFerma === "number" && ctx.giorniFerma > SOGLIA_FERMA_GIORNI && !chiusa(projectData)
      && (giorniApertura === null || giorniApertura > SOGLIA_FERMA_GIORNI)) {
    aggiungi(REGOLE.COMMESSA_FERMA, `Nessuna novità da ${ctx.giorniFerma} giorni: nessun rapportino, giornale, comunicazione o attività completata.`, {giorni:ctx.giorniFerma});
  }

  if (ctx.budget<=0 && (giorniApertura===null || giorniApertura>3)) aggiungi(REGOLE.BUDGET_MANCANTE, "Il Budget non è ancora stato impostato in Controllo Economico.");

  if (!ctx.hasCronoprogramma && (giorniApertura===null || giorniApertura>14)) aggiungi(REGOLE.CRONOPROGRAMMA_ASSENTE, "Nessuna fase di Cronoprogramma registrata.");

  if (ctx.computoSAL && (ctx.computoSAL.periodi||[]).length>0) {
    const ultimo = ctx.computoSAL.periodi[ctx.computoSAL.periodi.length-1];
    const gg = giorniDa(ultimo.data);
    if (gg!==null && gg>SOGLIA_SAL_FERMO_GIORNI) aggiungi(REGOLE.SAL_FERMO, `Ultimo SAL registrato ${gg} giorni fa.`, {giorni:gg});
  }

  const segAperte = (ctx.segnalazioni||[]).filter(s=>{
    if (s.stato==="chiusa") return false;
    const gg = giorniDa(s.createdAt);
    return gg!==null && gg>SOGLIA_SEGNALAZIONE_GIORNI;
  });
  if (segAperte.length>0) aggiungi(REGOLE.SEGNALAZIONE_APERTA, `${segAperte.length} segnalazion${segAperte.length===1?"e aperta":"i aperte"} da oltre ${SOGLIA_SEGNALAZIONE_GIORNI} giorni.`, {conteggio:segAperte.length});

  // Varianti (utils/varianti.js): eseguite ma senza importo da oltre la soglia → non entrano
  // nel totale commessa né nel margine; valorizzate ma non fatturate da oltre la soglia.
  const ferme = (stato) => (ctx.attivitaExtra||[]).filter(e => {
    if (statoVariante(e).id!==stato) return false;
    const gg = giorniDa(e.data);
    return gg!==null && gg>SOGLIA_EXTRA_GIORNI;
  });
  const daValorizzare = ferme("daValorizzare");
  // Eseguite (con importo) ma il cliente non le ha ancora accettate: costi già sostenuti,
  // ricavo fuori dal totale commessa finché non arriva l'accettazione.
  const daAccettare = ferme("inAttesa");
  if (daAccettare.length>0) {
    const tot = daAccettare.reduce((s,e)=>s+(importoVariante(e)||0),0);
    aggiungi(REGOLE.VARIANTI_DA_ACCETTARE, `${daAccettare.length} variant${daAccettare.length===1?"e eseguita":"i eseguite"} da oltre ${SOGLIA_EXTRA_GIORNI} giorni senza l'accettazione del cliente (€ ${tot.toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2})}): fuori dal totale commessa e dal margine.`, {conteggio:daAccettare.length});
  }
  if (daValorizzare.length>0) aggiungi(REGOLE.VARIANTI_DA_VALORIZZARE, `${daValorizzare.length} variant${daValorizzare.length===1?"e":"i"} da oltre ${SOGLIA_EXTRA_GIORNI} giorni senza importo: non entrano nel totale commessa.`, {conteggio:daValorizzare.length});

  if ((ctx.praticheInRitardo||[]).length>0) {
    const n = ctx.praticheInRitardo.length;
    aggiungi(REGOLE.PRATICA_FERMA, `${n} pratic${n===1?"a ferma":"he ferme"} oltre i tempi previsti: ${ctx.praticheInRitardo.map(p=>p.titolo).join(", ")}.`, {conteggio:n});
  }

  const daFatturare = ferme("valorizzata");
  if (daFatturare.length>0) {
    const tot = daFatturare.reduce((s,e)=>s+(importoVariante(e)||0),0);
    aggiungi(REGOLE.VARIANTI_DA_FATTURARE, `${daFatturare.length} variant${daFatturare.length===1?"e":"i"} da fatturare da oltre ${SOGLIA_EXTRA_GIORNI} giorni (€ ${tot.toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2})}).`, {conteggio:daFatturare.length});
  }

  if ((ctx.materialiInAttesa||[]).length>0) {
    const n = ctx.materialiInAttesa.length;
    aggiungi(REGOLE.MATERIALI_IN_ATTESA, `${n} richiest${n===1?"a":"e"} al magazzino in attesa da oltre ${SOGLIA_MATERIALI_GIORNI} giorni.`, {conteggio:n});
  }

  if (ctx.comunicazioniSenzaTipo>0) {
    const n = ctx.comunicazioniSenzaTipo;
    aggiungi(REGOLE.COMUNICAZIONI_SENZA_TIPO, `${n} comunicazion${n===1?"e":"i"} con il cliente senza tipo: indicare se ${n===1?"è una segnalazione":"sono segnalazioni"}.`, {conteggio:n});
  }

  if (ctx.oreDiverse?.length > 0) {
    const n = ctx.oreDiverse.length;
    aggiungi(REGOLE.ORE_DIVERSE, `${n} giornat${n===1?"a":"e"} in cui il rapportino indica ore diverse da quelle contabilizzate dalla fonte ore «${ctx.fonteOreLabel || "—"}» (es. ${ctx.oreDiverse[0].nome} il ${dataBreve(ctx.oreDiverse[0].data)}: ${String(ctx.oreDiverse[0].oreRapportino).replace(".", ",")} h nel rapportino, ${String(ctx.oreDiverse[0].oreContabilizzate).replace(".", ",")} h contabilizzate).`, {conteggio:n});
  }

  return {
    segnali,
    silenziati,
    segnaliAlta: segnali.filter(s=>s.gravita==="alta"),
    segnaliMedia: segnali.filter(s=>s.gravita==="media"),
    segnaliBassa: segnali.filter(s=>s.gravita==="bassa"),
  };
}

/** Dove porta il pulsante «Vai» di ciascun segnale. */
export const DESTINAZIONE_REGOLA = {
  pm_mancante:              { tab:"membri", testo:"Assegna" },
  fine_lavori_superata:     { sezione:"attivita", testo:"Vedi attività" },
  task_in_ritardo:          { sezione:"attivita", testo:"Vedi attività" },
  margine_a_rischio:        { tab:"economico", testo:"Controllo Economico" },
  noleggio_da_decidere:     { page:"registro-noleggi", testo:"Chiudi o proroga" },
  noleggio_sospeso:         { page:"registro-noleggi", testo:"Vedi noleggio" },
  noleggio_rettificato:     { page:"registro-noleggi", testo:"Vedi rettifica" },
  sicurezza_da_completare:  { tab:"sicurezza", testo:"Apri sicurezza" },
  ditta_non_in_regola:      { page:"documenti-subappaltatori", testo:"Documenti" },
  ditta_oltre_previsto:     { sezione:"ore", testo:"Vedi ditte" },
  commessa_ferma:           { sezione:"settimana", testo:"Vedi movimenti" },
  budget_mancante:          { tab:"economico", testo:"Imposta budget" },
  cronoprogramma_assente:   { tab:"cronoprogramma", testo:"Apri Cronoprogramma" },
  sal_fermo:                { tab:"sal", testo:"Apri SAL" },
  segnalazione_aperta:      { sezione:"cliente", testo:"Vedi segnalazioni" },
  varianti_da_valorizzare:  { sezione:"varianti", testo:"Vedi varianti" },
  varianti_da_accettare:    { sezione:"varianti", testo:"Vedi varianti" },
  pratica_ferma:            { tab:"pratiche", testo:"Apri pratiche" },
  varianti_da_fatturare:    { page:"da-fatturare", testo:"Registro fatturazione" },
  materiali_in_attesa:      { tab:"materiali", testo:"Apri richieste" },
  comunicazioni_senza_tipo: { tab:"comunicazioni", testo:"Apri Corrispondenza" },
  ore_diverse:              { sezione:"ore", testo:"Vedi ore" },
};

/** Rettifiche sui noleggi della commessa non ancora viste da un admin, dalla più recente. */
export function noleggiRettificati(noleggi = [], conImporti = true) {
  return noleggi.filter(n => n.rettificaDaVedere)
    .map(n => ({ n, r: rettificheDaVedere(n)[0] })).filter(x => x.r)
    .sort((a, b) => (b.r.il || "").localeCompare(a.r.il || ""))
    .map(({ n, r }) => ({ id: n.id, etichetta: etichettaNoleggio(n), testo: testoRettifica(r, conImporti, dataBreve), il: r.il }));
}

/** Noleggi della commessa da chiudere o prorogare (oltre la fine prevista, o aperti da molto
 * senza data), dal più costoso. Senza importi per chi non li vede. */
export function noleggiDaDecidere(noleggi = [], oggi, conImporti = true) {
  // Il «costo maturato» dell'avviso è quello di QUEL noleggio; il Controllo Economico somma
  // tutti i mezzi d'opera della commessa (anche quelli già chiusi). Se ce ne sono altri si
  // scrive anche il totale, così i due numeri non sembrano in contrasto.
  const totaleCommessa = noleggi.filter(n => n.categoria === "mezzo_opera").reduce((t, n) => t + costoNoleggio(n), 0);
  const euroT = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const conTotale = (testo, s) => (conImporti && totaleCommessa - s.costo > 0.005)
    ? testo.replace("costo maturato ", "costo maturato da questo noleggio ") + ` Tutti i noleggi della commessa: ${euroT(totaleCommessa)}.`
    : testo;
  return noleggi.map(n => ({ n, s: scadenzaNoleggio(n, oggi) }))
    .filter(x => x.s && (x.s.stato === "oltre" || x.s.stato === "senza_data" || (x.s.stato === "sospeso" && x.s.giorni > 0)))
    .sort((a, b) => b.s.costo - a.s.costo)
    .map(({ n, s }) => ({ id: n.id, etichetta: etichettaNoleggio(n), giorni: s.giorni, sospeso: s.stato === "sospeso",
      testo: conImporti ? conTotale(testoScadenzaNoleggio(s, dataBreve), s)
        : s.stato === "sospeso" ? `Sospeso dal ${dataBreve(s.sospensione.dal)} al ${dataBreve(s.sospensione.al)}${s.sospensione.motivo ? ` (${s.sospensione.motivo})` : ""}: doveva finire il ${dataBreve(s.prevista)}, alla ripresa va chiuso o prorogato.`
        : s.stato === "oltre" ? `Doveva finire il ${dataBreve(s.prevista)} ed è ancora aperto.` : `Aperto da ${s.giorni} giorni senza una data di fine prevista.` }));
}

// ═══════════════════════════════════════════════════════════════════════════
// ANALISI COMPLETA DI UNA COMMESSA (per la Cabina di Regia)
// ═══════════════════════════════════════════════════════════════════════════
const ETICHETTA_NOLEGGIO = (n) => [TIPI_MEZZO_OPERA[n.tipo]?.label || n.tipo || "Noleggio", n.identificativo, n.fornitore].filter(Boolean).join(" · ");

/**
 * @param {object} project - documento della commessa
 * @param {object} d - dati letti: { tasks, crono, computoSAL, budget, movimenti, ordini, segnalazioniCliente,
 *   comunicazioni, varianti, rapportini, giornale, sopralluoghi, eventi, noleggi, richiesteFatturazione,
 *   richiesteMateriali, pratiche, documentiPratiche, controlliPM } (ogni elenco con id)
 * @param {object} opz - { oggi, sicurezza:{attiva, aperte, daConfigurare, attivo, vociAperte}, moduli:{...},
 *   modoOrePrecedenti, fontOre, vediRiservati }
 */
export function analizzaCommessa(project, d, opz = {}) {
  const oggi = opz.oggi || toLocalDateStr(new Date());
  const settimanaFa = piuGiorni(oggi, -GIORNI_SETTIMANA);
  const traSettimana = piuGiorni(oggi, GIORNI_SETTIMANA);
  const moduli = opz.moduli || {};
  const L = (k) => d[k] || [];

  // ── Attività ──
  const tasks = L("tasks");
  const valide = tasks.filter(t => t.status !== "annullata");
  const completate = valide.filter(t => t.status === "completata").length;
  const inRitardo = tasks.filter(isTaskOverdue)
    .map(t => ({ id: t.id, titolo: t.title || "Attività", data: t.dueDate, giorni: giorniTra(t.dueDate, oggi), chi: t.assignedName || "", task: t }))
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));
  const inScadenza = tasks.filter(t => taskAperta(t) && t.status !== "sospesa" && !isTaskOverdue(t) && t.dueDate && t.dueDate >= oggi && t.dueDate <= traSettimana)
    .map(t => ({ id: t.id, titolo: t.title || "Attività", data: t.dueDate, giorni: giorniTra(oggi, t.dueDate), chi: t.assignedName || "", task: t }))
    .sort((a, b) => a.data.localeCompare(b.data));
  const attivita = { totale: valide.length, completate, aperte: valide.length - completate,
    perc: valide.length ? Math.round(completate / valide.length * 100) : null, inRitardo, inScadenza };

  // Ore dalla fonte dell'azienda (servono all'economia — W453 — e alla sezione Ore più sotto)
  const fonteOre = FONTI_ORE[opz.fonteOre] ? opz.fonteOre : "programmazione";
  const nomiUtenti = Object.fromEntries(L("utenti").map(u => [u.id, u.displayName || u.email || ""]));
  const vociRapportini = oreDaRapportini(L("rapportini"), opz.modoOrePrecedenti || "durata");
  const vociFonte = vociConDiario(fonteOre === "rapportini" ? vociRapportini
    : fonteOre === "presenze" ? oreDaPresenze(L("movimenti"))
    : oreDaProgrammazione(L("programmazioni"), project?.id, nomiUtenti),
    // diario «Le mie ore» (W403): null = non leggibile da chi guarda, si resta come prima
    fonteOre, { blocchi: Array.isArray(d.diario) ? d.diario : null, dipendenti: L("dipendenti"), nomi: nomiUtenti });
  // ── Varianti ed economia (stesse regole del Controllo Economico) ──
  const variantiValide = L("varianti").filter(v => statoVariante(v).id !== "esclusa");
  const rVar = riepilogoVarianti(variantiValide);
  const elencoVarianti = variantiValide.map(v => ({
    id: v.id, titolo: v.titolo || "Variante", data: isoDa(v.data) || isoDa(v.createdAt), stato: statoVariante(v),
    origine: ORIGINI_VARIANTE[v.origine] || null, importo: importoVariante(v), autorizzazione: v.autorizzazione || "",
  })).sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const budgetDoc = { ...(d.budget || {}), variantiCalcolate: rVar };
  // W453: manodopera, «Le mie ore» e noleggi calcolati adesso, come il Controllo Economico
  // (prima valevano all'ultima apertura del Controllo Economico)
  const costiOra = unisciCostiCalcolati(costiCalcolatiDaVoci({ voci: vociFonte, dipendenti: L("dipendenti"), fonteOre,
    noleggi: L("noleggi").filter(n => n.categoria === "mezzo_opera").reduce((t, n) => t + costoNoleggio(n), 0) }),
    { voci: vociFonte, diarioLeggibile: Array.isArray(d.diario), dipendentiLeggibili: L("dipendenti").length > 0 }, (d.budget || {}).costiCalcolati || null);
  const eco = economiaSintetica({ budget: budgetDoc, movimenti: L("movimenti"), ordini: L("ordini"), projectId: project?.id, fontOre: fonteOre, costiCalcolati: costiOra });
  const economia = { contratto: importoCommessa(d.budget), varianti: rVar.totale, totale: importoCommessa(d.budget) + rVar.totale,
    ricavi: eco.ricavi, costi: eco.costi, margine: eco.margine, marginePercent: eco.marginePercent, budgetPresente: eco.ricavi > 0 };

  // ── SAL e fatturazione ──
  const computo = d.computoSAL || null;
  const periodi = computo?.periodi || [];
  const sal = computo ? calcolaSAL(computo.righe || [], periodi, computo.fatturato, computo.richiestoInAttesa) : null;
  const salvati = periodi.filter(p => p.stato === "salvato");
  const ultimoSal = salvati.length ? isoDa(salvati[salvati.length - 1].data) : "";
  // Richieste di fatturazione: chi le gestisce le vede tutte, il PM solo le sue (regole 57).
  // I totali della commessa per il PM vengono dal budget (utils/fatturazioneCommessa.js).
  const richieste = L("richiesteFatturazione").filter(r => r.stato !== "annullata");
  const richiesteInAttesa = richieste.filter(r => r.stato === "da_fatturare" || r.stato === "in_lavorazione")
    .map(r => ({ id: r.id, numero: r.numero || "", importo: numIt(r.importoRichiesto), data: r.dataRichiesta || isoDa(r.createdAt), stato: r.stato, tipo: r.tipo || "" }));
  const richiesteFatturate = richieste.filter(r => r.stato === "fatturata");
  const totCommessa = !opz.vedeTutteFatturazioni ? d.budget?.fatturazioneCalcolata : null;
  const fatturatoRichieste = richiesteFatturate.reduce((s, r) => s + numIt(r.importoRichiesto), 0);
  const inAttesaRichieste = richiesteInAttesa.reduce((s, r) => s + r.importo, 0);
  const fatturazione = {
    fatturato: Math.max(fatturatoRichieste, totCommessa?.importoFatturato || 0) + rVar.fatturato,
    inAttesa: Math.max(inAttesaRichieste, totCommessa?.importoInAttesa || 0),
    richiesteInAttesa, nInAttesa: Math.max(richiesteInAttesa.length, totCommessa?.nInAttesa || 0),
    nFatturate: Math.max(richiesteFatturate.length, totCommessa?.nFatturate || 0),
    soloLeMie: !opz.vedeTutteFatturazioni,
    variantiDaFatturare: rVar.daFatturare, nVariantiDaFatturare: rVar.nDaFatturare,
    sal: sal ? { avanzamento: sal.avanzamentoComplessivo, maturato: sal.salMaturato, daFatturare: Math.max(0, sal.daFatturare), nonRichiesto: sal.nonRichiesto, n: salvati.length, ultimo: ultimoSal } : null,
  };

  // ── Ore e personale: dalla STESSA fonte del Controllo Economico (utils/oreCommessa.js) ──
  // Programmazione, Rapportini o Presenze, come impostato dall'azienda. I rapportini, quando
  // non sono la fonte, servono da riscontro: ore diverse per la stessa persona e lo stesso
  // giorno vengono segnalate. Il Giornale di cantiere conta solo per «chi è stato in cantiere».
  const rOre = riepilogoOre(vociFonte, { rapportini: vociRapportini, fonte: fonteOre });
  const ug = uominiGiornoPresunti(project);
  const presenti = new Set();
  [...rOre.elenco, ...vociRapportini].forEach(v => { if (v.data >= settimanaFa && v.data <= oggi) presenti.add(v.nome); });
  L("giornale").forEach(g => { const data = isoDa(g.data); if (data >= settimanaFa && data <= oggi) (g.manodopera || []).forEach(m => { if (String(m.nome || "").trim()) presenti.add(String(m.nome).trim()); }); });
  const ore = {
    fonte: fonteOre, fonteLabel: FONTI_ORE[fonteOre],
    // con le Presenze i dati arrivano dai movimenti economici: senza accesso alla parte economica non si leggono
    disponibile: fonteOre !== "presenze" || !!opz.mostraImporti,
    giornate: rOre.giornate, ore: rOre.ore, previste: ug.interni || null,
    persone: rOre.persone, differenze: rOre.differenze,
    settimana: [...presenti],
    // grafico (W409): ore per settimana, ultime 10 settimane
    settimane: orePerSettimana(rOre.elenco, { oggi: new Date(oggi + "T12:00:00"), settimane: 10 }),
  };

  // ── Cliente ──
  const comunicazioni = L("comunicazioni");
  const segnAssist = L("segnalazioniCliente");
  const cliente = {
    aperte: comunicazioni.filter(segnalazioneAperta).map(c => ({ id: c.id, oggetto: c.oggetto || "Segnalazione", data: isoDa(c.dataComunicazione) || isoDa(c.createdAt), mittente: c.mittente || "" }))
      .sort((a, b) => (a.data || "").localeCompare(b.data || "")),
    senzaTipo: comunicazioni.filter(senzaTipo).map(c => ({ id: c.id, oggetto: c.oggetto || "Comunicazione", data: isoDa(c.dataComunicazione) || isoDa(c.createdAt) })),
    nComunicazioni: comunicazioni.filter(eCliente).length,
    assistenzaAperte: segnAssist.filter(s => s.stato !== "chiusa").map(s => ({ id: s.id, oggetto: s.titolo || s.oggetto || s.descrizione || "Segnalazione", data: isoDa(s.createdAt) || isoDa(s.data), stato: s.stato || "" })),
    assistenzaTot: segnAssist.length,
  };

  // ── Pratiche ──
  const documenti = L("documentiPratiche");
  const elencoPratiche = L("pratiche").filter(p => p.stato !== "archiviata").map(p => {
    const av = avanzamento(righePratica(p, documenti, { vediRiservati: !!opz.vediRiservati }));
    const it = statoIter(p, { documentiCompleti: av.completa, oggi, inizioIso: isoDa(p.createdAt) || oggi });
    return { id: p.id, titolo: p.titolo || p.modelloLabel || "Pratica", completata: it.completata, fase: it.attuale?.fase?.label || "",
      inRitardo: !!it.attuale?.inRitardo, escalation: !!it.attuale?.escalation, giorniFerma: it.attuale?.giorniFerma || 0,
      ricevuti: av.ricevuti, necessari: av.necessari, scadenze: it.scadenze || [] };
  });
  const pratiche = { elenco: elencoPratiche, aperte: elencoPratiche.filter(p => !p.completata), inRitardo: elencoPratiche.filter(p => !p.completata && p.inRitardo) };

  // ── Materiali e noleggi ──
  const materiali = L("richiesteMateriali").filter(r => r.stato === "in_attesa" || r.stato === "in_lavorazione")
    .map(r => ({ id: r.id, data: isoDa(r.createdAt), chi: r.richiestaFromName || "", righe: (r.righe || []).length, urgenza: r.urgenza || "", stato: r.stato }))
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));
  const noleggi = L("noleggi").filter(isNoleggioAttivo)
    .map(n => ({ id: n.id, etichetta: ETICHETTA_NOLEGGIO(n), dal: n.dataInizio || "", finePrevista: n.dataFinePrevista || "", costo: costoNoleggio(n),
      sospensione: (n.sospensioni || []).find(x => x && x.dal && x.al && oggi >= x.dal && oggi <= x.al) || null }));

  // Materiali nei costi della commessa (come nel Controllo Economico): quelli inseriti a mano
  // nella categoria Materiali (e i rientri in magazzino, in negativo) più gli ordini d'acquisto
  // avviati. Si leggono solo con la parte economica (movimenti e ordini).
  const movMateriali = L("movimenti").filter(m => m.tipo !== "ricavo" && !m.eliminato && m.categoriaId === "materiali")
    .map(m => ({ id: m.id, data: dataMovimento(m), descrizione: m.dati?.descrizione || m.descrizione || "Materiale",
      fornitore: m.dati?.fornitore || "", quantita: m.dati?.quantita || "", importo: numIt(m.importo),
      origine: m.origineRientroId ? "Rientro in magazzino" : "Inserito in Controllo Economico" }))
    .sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const odaMateriali = L("ordini").filter(o => !o.eliminato && STATI_ODA_CONTEGGIATI.includes(o.stato) && (o.projectId === project?.id || o.commessaId === project?.id))
    .map(o => ({ id: o.id, numero: o.numeroODA || o.numeroOda || "", fornitore: o.fornitoreNome || "", data: o.data || isoDa(o.createdAt), stato: o.stato || "", importo: imponibileOda(o) }))
    .sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const materialiCosti = {
    disponibile: !!opz.mostraImporti && !!moduli.economico,
    movimenti: movMateriali, oda: odaMateriali,
    totaleMovimenti: movMateriali.reduce((x, m) => x + m.importo, 0),
    totaleOda: odaMateriali.reduce((x, o) => x + o.importo, 0),
  };

  // ── Sicurezza (dalla scheda della commessa, calcolata dal componente) ──
  const sicurezza = opz.sicurezza || { attiva: false };

  // ── Settimana: cosa è successo, cosa arriva ──
  const passati = [], prossimi = [];
  const nelPassato = (iso) => iso && iso >= settimanaFa && iso <= oggi;
  const nelFuturo = (iso) => iso && iso >= oggi && iso <= traSettimana;
  tasks.forEach(t => { const c = isoDa(t.completedAt); if (t.status === "completata" && nelPassato(c)) passati.push({ data: c, icona: "✅", testo: `Attività completata: ${t.title || ""}`, vai: { task: t } }); });
  L("rapportini").forEach(r => { const x = isoDa(r.data); if (nelPassato(x)) passati.push({ data: x, icona: "📝", testo: `Rapportino ${r.numero || ""}${r.descrizioneBreve ? `: ${r.descrizioneBreve}` : ""}`, vai: { tab: "rapportini" } }); });
  L("giornale").forEach(g => { const x = isoDa(g.data); if (nelPassato(x)) passati.push({ data: x, icona: "📒", testo: "Giornale di cantiere compilato", vai: { tab: "giornale" } }); });
  comunicazioni.forEach(c => { const x = isoDa(c.dataComunicazione) || isoDa(c.createdAt); if (nelPassato(x)) passati.push({ data: x, icona: "✉️", testo: `${c.direzione === "inviata" ? "Inviata" : c.direzione === "ricevuta" ? "Ricevuta" : "Comunicazione"}: ${c.oggetto || "senza oggetto"}`, vai: { tab: "comunicazioni", id: c.id } }); });
  elencoVarianti.forEach(v => { if (nelPassato(v.data)) passati.push({ data: v.data, icona: "➕", testo: `Variante: ${v.titolo}`, vai: { tab: "extra", id: v.id } }); });
  salvati.forEach((p, i) => { const x = isoDa(p.data); if (nelPassato(x)) passati.push({ data: x, icona: "📐", testo: `SAL n. ${p.numero || i + 1} registrato`, vai: { tab: "sal" } }); });
  L("sopralluoghi").forEach(s => { const x = isoDa(s.data); if (nelPassato(x)) passati.push({ data: x, icona: "🔍", testo: `Sopralluogo${s.numero ? ` ${s.numero}` : ""}`, vai: { tab: "sopralluogo" } }); });
  if (materialiCosti.disponibile) {
    movMateriali.forEach(m => { if (nelPassato(m.data)) passati.push({ data: m.data, icona: m.importo < 0 ? "↩️" : "🔧", testo: `${m.importo < 0 ? "Rientro materiale" : "Materiale"}: ${m.descrizione}`, vai: { tab: "economico" } }); });
    odaMateriali.forEach(o => { if (nelPassato(o.data)) passati.push({ data: o.data, icona: "🧾", testo: `Ordine d'acquisto ${o.numero}${o.fornitore ? ` — ${o.fornitore}` : ""}`, vai: { page: "centro-acquisti" } }); });
  }
  segnAssist.forEach(s => { const x = isoDa(s.createdAt); if (nelPassato(x)) passati.push({ data: x, icona: "🛠️", testo: `Segnalazione di assistenza: ${s.titolo || s.oggetto || ""}`, vai: { page: "segnalazioni-cliente" } }); });

  inScadenza.forEach(t => prossimi.push({ data: t.data, icona: "⏰", testo: `Scade: ${t.titolo}${t.chi ? ` (${t.chi})` : ""}`, vai: { task: t.task } }));
  L("eventi").forEach(e => { const x = isoDa(e.dueDate) || isoDa(e.data); if (nelFuturo(x)) prossimi.push({ data: x, icona: "📅", testo: `${e.titolo || "Evento"}${e.oraInizio ? ` alle ${e.oraInizio}` : ""}`, vai: { tab: "calendar" } }); });
  L("crono").forEach(f => {
    if (nelFuturo(isoDa(f.inizio))) prossimi.push({ data: isoDa(f.inizio), icona: "▶️", testo: `Inizia la fase: ${f.nome || ""}`, vai: { tab: "cronoprogramma" } });
    if (nelFuturo(isoDa(f.fine))) prossimi.push({ data: isoDa(f.fine), icona: "🏁", testo: `Termina la fase: ${f.nome || ""}`, vai: { tab: "cronoprogramma" } });
  });
  noleggi.forEach(n => { if (nelFuturo(n.finePrevista)) prossimi.push({ data: n.finePrevista, icona: "🏗️", testo: `Fine noleggio prevista: ${n.etichetta}`, vai: { page: "registro-noleggi" } }); });
  L("controlliPM").filter(c => c.stato === "Aperto" || c.stato === "Da sollecitare").forEach(c => { if (nelFuturo(c.dataVerifica)) prossimi.push({ data: c.dataVerifica, icona: "🎯", testo: `Controllo PM: ${c.titolo}`, vai: { sezione: "controlli" } }); });
  elencoPratiche.forEach(p => p.scadenze.forEach(s => { if (nelFuturo(s.data)) prossimi.push({ data: s.data, icona: "📁", testo: `${p.titolo}: ${s.label}`, vai: { tab: "pratiche" } }); }));
  const fine = isoDa(project?.dataFine);
  if (!chiusa(project) && nelFuturo(fine)) prossimi.push({ data: fine, icona: "🏁", testo: "Fine lavori prevista", vai: { sezione: "chiusura" } });
  passati.sort((a, b) => b.data.localeCompare(a.data));
  prossimi.sort((a, b) => a.data.localeCompare(b.data));

  // ── Ultima novità sulla commessa ──
  const date = [
    ...tasks.flatMap(t => [isoDa(t.completedAt), isoDa(t.createdAt)]),
    ...L("rapportini").map(r => isoDa(r.data)), ...L("giornale").map(g => isoDa(g.data)),
    ...comunicazioni.map(c => isoDa(c.dataComunicazione) || isoDa(c.createdAt)),
    ...elencoVarianti.map(v => v.data), ...salvati.map(p => isoDa(p.data)),
    ...L("sopralluoghi").map(s => isoDa(s.data)), ...segnAssist.map(s => isoDa(s.createdAt)),
    ...L("richiesteMateriali").map(r => isoDa(r.createdAt)),
    ...movMateriali.map(m => m.data), ...odaMateriali.map(o => o.data),
  ].filter(x => x && x <= oggi);
  const ultimaAttivita = date.length ? date.reduce((a, b) => (a > b ? a : b)) : isoDa(project?.createdAt);
  const giorniFerma = ultimaAttivita ? giorniTra(ultimaAttivita, oggi) : null;

  // ── Ditte esterne (W413) ──
  const ditte = ditteDellaCommessa(project, { giorni: L("giornale"), movimenti: L("movimenti"), vedeImporti: !!opz.mostraImporti });
  const trentaGiorniFa = piuGiorni(oggi, -30);
  const assegnate = new Set((project?.impreseEsterne || []).map(i => i.subappaltatoreId).filter(Boolean));
  const ditteNonInRegola = chiusa(project) || !opz.idoneita ? [] : ditte
    .filter(x => x.subappaltatoreId && (assegnate.has(x.subappaltatoreId) || (x.ultimo && x.ultimo >= trentaGiorniFa)))
    .map(x => ({ ...x, info: opz.idoneita.get(x.subappaltatoreId) }))
    .filter(x => x.info && x.info.semaforo.colore === "rosso")
    .map(x => ({ nome: x.nome, subappaltatoreId: x.subappaltatoreId, testo: testoProblemiDitta(x.info.problemi), problemi: x.info.problemi }));
  const ditteOltre = ditte.filter(x => x.oltre);

  // ── Segnali ──
  const segnaliRisultato = valutaSegnaliCommessa(project, {
    oggi, tasks, hasCronoprogramma: L("crono").length > 0 || !moduli.cronoprogramma, computoSAL: computo,
    // margine e budget solo dove il Controllo Economico è attivo e per chi vede gli importi
    budget: moduli.economico && opz.mostraImporti ? eco.ricavi : 1, costiSostenuti: moduli.economico && opz.mostraImporti ? eco.costi : 0,
    segnalazioni: [
      ...segnAssist,
      ...comunicazioni.filter(segnalazioneAperta).map(c => ({ stato: "aperta", createdAt: c.dataComunicazione || c.createdAt })),
    ],
    attivitaExtra: variantiValide, giorniFerma,
    praticheInRitardo: pratiche.inRitardo, sicurezza,
    materialiInAttesa: materiali.filter(r => r.stato === "in_attesa" && r.data && giorniTra(r.data, oggi) > SOGLIA_MATERIALI_GIORNI),
    noleggiDaDecidere: noleggiDaDecidere(L("noleggi"), oggi, !!opz.mostraImporti),
    noleggiRettificati: noleggiRettificati(L("noleggi"), !!opz.mostraImporti),
    comunicazioniSenzaTipo: cliente.senzaTipo.length,
    oreDiverse: ore.disponibile ? ore.differenze : [], fonteOreLabel: ore.fonteLabel,
    ditteNonInRegola, ditteOltre,
  });

  // ── Salute: quattro semafori ──
  const semafori = [];
  {
    const perc = attivita.perc ?? 0;
    let s;
    if (!fine && attivita.totale === 0) s = { colore: "grigio", testo: "Date dei lavori e attività non ancora indicate" };
    else if (fine && fine < oggi && !chiusa(project) && attivita.aperte > 0) s = { colore: "rosso", testo: `Fine lavori superata da ${giorniTra(fine, oggi)} giorni` };
    else if (inRitardo.length >= 3) s = { colore: "rosso", testo: `${inRitardo.length} attività in ritardo` };
    else if (inRitardo.length > 0) s = { colore: "giallo", testo: `${inRitardo.length} attività in ritardo` };
    else if (fine && !chiusa(project) && giorniTra(oggi, fine) <= GIORNI_SETTIMANA && perc < 80) s = { colore: "giallo", testo: `Fine lavori tra ${giorniTra(oggi, fine)} giorni, attività al ${perc}%` };
    else s = { colore: "verde", testo: fine ? `Nei tempi · fine lavori ${dataBreve(fine)}` : "Nessuna attività in ritardo" };
    semafori.push({ id: "tempi", label: "Tempi", ...s, vai: { sezione: "attivita" } });
  }
  if (opz.mostraImporti && moduli.economico) {
    const m = eco.marginePercent;
    let s;
    if (!economia.budgetPresente) s = { colore: "grigio", testo: "Budget non impostato" };
    else if (m < 0) s = { colore: "rosso", testo: `In perdita: margine ${m.toFixed(1)}%` };
    else if (m < SOGLIA_MARGINE_RISCHIO) s = { colore: "giallo", testo: `Margine ${m.toFixed(1)}% (sotto il ${SOGLIA_MARGINE_RISCHIO}%)` };
    else s = { colore: "verde", testo: `Margine ${m.toFixed(1)}%` };
    semafori.push({ id: "costi", label: "Costi", ...s, vai: { tab: "economico" } });
  }
  {
    const vecchie = [...cliente.aperte, ...cliente.assistenzaAperte].filter(x => x.data && giorniTra(x.data, oggi) > SOGLIA_SEGNALAZIONE_GIORNI);
    const nAperte = cliente.aperte.length + cliente.assistenzaAperte.length;
    let s;
    if (vecchie.length) s = { colore: "rosso", testo: `${vecchie.length} segnalazion${vecchie.length === 1 ? "e" : "i"} senza seguito da oltre ${SOGLIA_SEGNALAZIONE_GIORNI} giorni` };
    else if (nAperte) s = { colore: "giallo", testo: `${nAperte} segnalazion${nAperte === 1 ? "e" : "i"} da gestire` };
    else if (cliente.senzaTipo.length) s = { colore: "giallo", testo: `${cliente.senzaTipo.length} comunicazion${cliente.senzaTipo.length === 1 ? "e" : "i"} senza tipo` };
    else s = { colore: "verde", testo: "Nessuna segnalazione aperta" };
    semafori.push({ id: "cliente", label: "Cliente", ...s, vai: { sezione: "cliente" } });
  }
  {
    const sic = sicurezza.attiva;
    const docMancanti = pratiche.aperte.filter(p => p.necessari > p.ricevuti).length;
    let s;
    if (ditteNonInRegola.length) s = { colore: "rosso", testo: `${ditteNonInRegola.length === 1 ? `${ditteNonInRegola[0].nome}: documenti` : `${ditteNonInRegola.length} imprese esterne con documenti`} non in regola` };
    else if (sic && sicurezza.daConfigurare) s = { colore: "rosso", testo: "Sicurezza di cantiere da impostare" };
    else if (pratiche.aperte.some(p => p.escalation)) s = { colore: "rosso", testo: "Pratica ferma da molto oltre i tempi" };
    else if (sic && sicurezza.aperte > 0) s = { colore: "giallo", testo: `Sicurezza: ${sicurezza.aperte} adempiment${sicurezza.aperte === 1 ? "o" : "i"} da completare` };
    else if (pratiche.inRitardo.length) s = { colore: "giallo", testo: `${pratiche.inRitardo.length} pratic${pratiche.inRitardo.length === 1 ? "a" : "he"} in ritardo` };
    else if (docMancanti) s = { colore: "giallo", testo: `${docMancanti} pratic${docMancanti === 1 ? "a" : "he"} con documenti mancanti` };
    else if (sic || elencoPratiche.length) s = { colore: "verde", testo: "Pratiche e sicurezza in regola" };
    else s = { colore: "grigio", testo: "Nessuna pratica o sicurezza da seguire" };
    semafori.push({ id: "documenti", label: "Documenti", ...s, vai: { sezione: "documenti" } });
  }

  // ── Checklist di chiusura (solo le voci che riguardano questa commessa) ──
  const chiusura = [];
  const voce = (id, label, ok, dettaglio, vai) => chiusura.push({ id, label, ok, dettaglio: ok ? "" : dettaglio, vai });
  voce("attivita", "Attività completate", attivita.aperte === 0, `${attivita.aperte} attività ancora apert${attivita.aperte === 1 ? "a" : "e"}`, { sezione: "attivita" });
  if (moduli.extra || variantiValide.length) voce("varianti", "Varianti tutte valorizzate", rVar.nDaValorizzare === 0, `${rVar.nDaValorizzare} senza importo`, { sezione: "varianti" });
  if (moduli.extra || richieste.length || variantiValide.length) voce("fatturazione", "Niente in sospeso da fatturare", rVar.nDaFatturare === 0 && fatturazione.nInAttesa === 0,
    [rVar.nDaFatturare ? `${rVar.nDaFatturare} variant${rVar.nDaFatturare === 1 ? "e" : "i"} da fatturare` : "", fatturazione.nInAttesa ? `${fatturazione.nInAttesa} richiest${fatturazione.nInAttesa === 1 ? "a" : "e"} di fatturazione in attesa` : ""].filter(Boolean).join(", "), { page: "da-fatturare" });
  if (computo) voce("sal", "SAL finale registrato", !!sal && sal.avanzamentoComplessivo >= 99.5, `SAL al ${Math.round(sal?.avanzamentoComplessivo || 0)}%`, { tab: "sal" });
  const nSegnAperte = cliente.aperte.length + cliente.assistenzaAperte.length;
  voce("cliente", "Comunicazioni del cliente gestite", cliente.aperte.length + cliente.assistenzaAperte.length + cliente.senzaTipo.length === 0,
    [nSegnAperte ? `${nSegnAperte} segnalazion${nSegnAperte === 1 ? "e aperta" : "i aperte"}` : "", cliente.senzaTipo.length ? `${cliente.senzaTipo.length} senza tipo` : ""].filter(Boolean).join(", "), { sezione: "cliente" });
  if (elencoPratiche.length) voce("pratiche", "Pratiche completate", pratiche.aperte.length === 0, `${pratiche.aperte.length} ancora apert${pratiche.aperte.length === 1 ? "a" : "e"}`, { tab: "pratiche" });
  if (sicurezza.attiva) voce("sicurezza", "Sicurezza di cantiere completata", !sicurezza.daConfigurare && sicurezza.aperte === 0, sicurezza.daConfigurare ? "da impostare" : `${sicurezza.aperte} adempimenti aperti`, { tab: "sicurezza" });
  if (noleggi.length || L("noleggi").length) voce("noleggi", "Noleggi chiusi", noleggi.length === 0, `${noleggi.length} ancora attiv${noleggi.length === 1 ? "o" : "i"}`, { page: "registro-noleggi" });
  if (L("richiesteMateriali").length) voce("materiali", "Richieste al magazzino evase", materiali.length === 0, `${materiali.length} ancora in corso`, { tab: "materiali" });
  const controlliAperti = L("controlliPM").filter(c => c.stato === "Aperto" || c.stato === "Da sollecitare");
  if (L("controlliPM").length) voce("controlli", "Controlli PM chiusi", controlliAperti.length === 0, `${controlliAperti.length} ancora apert${controlliAperti.length === 1 ? "o" : "i"}`, { sezione: "controlli" });
  const vicinaAllaFine = !chiusa(project) && ((fine && giorniTra(oggi, fine) <= 14) || (attivita.perc !== null && attivita.perc >= 80));

  // ── Impostazione (quanto la commessa è impostata bene, non quanto è avanzata) ──
  const criteri = [
    { label: "Project Manager", ok: !!project?.responsabileUid, peso: 25 },
    { label: "Date di inizio e fine lavori", ok: !!(project?.dataInizio && project?.dataFine), peso: 20 },
    // la sede va indicata solo se il cliente ne ha più di una: con una sola sede vale quella
    ...(project?.senzaCliente ? [] : [{
      label: opz.sedeEffettiva?.nSedi === 0 ? "Indirizzo del cliente o sede dei lavori" : "Sede dei lavori (il cliente ha più sedi: indica quale)",
      ok: opz.sedeEffettiva ? !!opz.sedeEffettiva.sede : !!(project?.sede || project?.sitoCliente), peso: 10 }]),
    { label: "Cliente", ok: !!project?.clienteNome, peso: 10 },
    ...(moduli.economico ? [{ label: "Budget", ok: eco.ricavi > 0, peso: 20 }] : []),
    ...(moduli.cronoprogramma ? [{ label: "Cronoprogramma", ok: L("crono").length > 0, peso: 15 }] : []),
  ];
  const pesoTot = criteri.reduce((s, c) => s + c.peso, 0);
  const impostazione = { perc: Math.round(criteri.reduce((s, c) => s + (c.ok ? c.peso : 0), 0) / pesoTot * 100), mancanti: criteri.filter(c => !c.ok).map(c => c.label) };

  return {
    oggi, attivita, economia, varianti: { elenco: elencoVarianti, riepilogo: rVar }, fatturazione, ore, cliente, pratiche, ditte, ditteNonInRegola,
    materiali, materialiCosti, noleggi, sicurezza, settimana: { passati, prossimi }, ultimaAttivita, giorniFerma,
    segnali: segnaliRisultato, semafori, chiusura, vicinaAllaFine, impostazione,
    controlliAperti, controlliScaduti: controlliAperti.filter(c => c.dataVerifica && c.dataVerifica < oggi),
  };
}
